import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppStore } from '../../../store/applicationState'
import { DropDownImageManage } from '../../../components/ui/adminComponents/DropDownImageManage'
import { TTenders } from '../../../store/admin/tenders/types'
import {
  getTenderItemsForDeleteChildParsed,
  getTenderItemsForDeleteParsed,
} from '../../../utils/tendersUtils/tenderDataHelper'
import {
  DeleteAllImages,
  DeleteOneImage,
  GetTenderGalleryList,
} from '../../../store/admin/settings/mediaManagement/actions'
import { SetLoader } from '../../../store/globalState/action'
import { useImageSync } from '../../../hooks/useImageSync'
import { Spinner } from '../../../svg/Spinner'
import { addNewParams } from '../../../utils/helpers'
import { Checkbox } from '../../../components/ui/Checkbox'
import { ImageManagementTable } from '../../../components/ui/adminComponents/ImageManagementTable'

const deleteImageThead = {
  image: 'Main media',
  sku: 'SKU',
  brand: 'Item',
  media: '# Media',
  action: 'Actions',
}

const deleteImageTheadChild = {
  image_preview: 'Media preview',
  image_name: 'Media name',
  handle: 'Handle',
  action: 'Actions',
}

const tableCellWidth = [155, 140, 260, 60, 100]

export const ImageManagement = () => {
  const dispatch = useDispatch()
  const mediaManagement = useSelector((store: AppStore) => store.mediaManagement)
  const tenders = useSelector((store: AppStore) => store.tenders)
  const { startGoogleDriveImageSync, imageSyncLoading } = useImageSync()
  const [withoutImages, setWithoutImages] = useState(false)

  const [selectedTender, setSelectedTender] = useState<TTenders | null>(null)
  const [childIndex, setChildIndex] = useState<number[]>([])

  useEffect(() => {
    if (selectedTender) dispatch(GetTenderGalleryList.request({ idTender: selectedTender.id }))
  }, [selectedTender])

  useEffect(() => {
    if (selectedTender && tenders.data) {
      const tender = tenders.data.find((tenderFromList) => tenderFromList.id === selectedTender.id)
      if (tender) setSelectedTender(tender)
    }
  }, [tenders.data])

  /// //////////////////////////////////////////////////////////////////////////////////
  const onDeleteImg = (id: number) => {
    if (selectedTender) {
      dispatch(SetLoader())
      dispatch(
        DeleteOneImage.request({
          idTender: selectedTender?.id,
          idImage: id,
          callBack: (success) => {
            if (success) dispatch(GetTenderGalleryList.request({ idTender: selectedTender.id }))
          },
        })
      )
    }
  }

  const onDeleteAll = (id: number) => {
    if (selectedTender) {
      dispatch(SetLoader())
      dispatch(
        DeleteAllImages.request({
          idTender: selectedTender?.id,
          idItem: id,
          callBack: (success) => {
            if (success) dispatch(GetTenderGalleryList.request({ idTender: selectedTender.id }))
          },
        })
      )
    }
  }

  const tenderItemsForDelete = useMemo(() => {
    let data = mediaManagement.data.galleryList
    if (data) {
      if (withoutImages) data = data.filter((item) => !item.product.gallery?.length)
    }

    return data ? getTenderItemsForDeleteParsed(data, childIndex) : null
  }, [mediaManagement.data.galleryList, childIndex, withoutImages])

  const tenderItemsForDeleteChild = useMemo(() => {
    if (!mediaManagement.data.galleryList) return null
    let data = mediaManagement.data.galleryList
    if (data) {
      if (withoutImages) data = data.filter((item) => !item.product.gallery?.length)
    }
    const result = new Map<number, object[]>()
    data.forEach((value) => {
      if (value.product.gallery)
        result.set(value.item_id, getTenderItemsForDeleteChildParsed(value.product.gallery, onDeleteImg))
      else result.set(value.item_id, [])
    })
    return result
  }, [mediaManagement.data.galleryList, withoutImages])

  const onSetTenderId = (tender: TTenders | null) => {
    addNewParams('tender_id', `${tender?.id}`)
    setSelectedTender(tender)
  }

  const resyncWithoutImages = () => {
    startGoogleDriveImageSync(selectedTender?.id || 0, true)
  }

  const clickOnlyWithoutImages = () => {
    setWithoutImages(!withoutImages)
    setChildIndex([])
  }

  return (
    <>
      <div className='flex flex-col md:flex-row gap-2 md:justify-between items-end md:items-center mb-8'>
        <DropDownImageManage tenderId={selectedTender?.id || 0} setTenderId={onSetTenderId} />

        <button
          type='button'
          onClick={() => startGoogleDriveImageSync(selectedTender?.id || 0)}
          className='push-from-ns text-white px-6 py-2 bg-lightblue-600 rounded-full h-max hover:bg-lightblue-700 flex justify-center items-center relative w-full smallMobile:w-[180px] min-h-10'
          disabled={imageSyncLoading || !selectedTender}
        >
          {imageSyncLoading ? <Spinner style={{ color: 'white' }} /> : 'Sync Goggle Drive'}
        </button>
      </div>

      {selectedTender && selectedTender.google_drive_sync_status && mediaManagement.data.galleryList ? (
        <div className='bg-gray-50 p-3 sm:px-6 mb-2 flex flex-col smallMobile:flex-row gap-6 justify-between items-start smallMobile:items-center'>
          <div className='flex flex-col lg:flex-row gap-2 lg:gap-6'>
            <div className='text-black font-semibold'>
              Sync status:
              <span className='capitalize pl-1 text-green-500'>Completed</span>
            </div>

            <div className='text-green-600 font-semibold'>
              Success:
              <span className='capitalize pl-1'>
                {
                  mediaManagement.data.galleryList.filter((item) => item.product.google_drive_sync_status === 'success')
                    .length
                }
              </span>
            </div>

            <div className='text-red-600 font-semibold'>
              Failed:
              <span className='capitalize pl-1'>
                {
                  mediaManagement.data.galleryList.filter((item) => item.product.google_drive_sync_status === 'failed')
                    .length
                }
              </span>
            </div>
          </div>

          <div className='flex flex-col lg:flex-row gap-2 w-full smallMobile:w-auto'>
            <Checkbox
              label='Only without images'
              isChecked={withoutImages}
              setIsChecked={clickOnlyWithoutImages}
              classname='smallMobile:!flex-row-reverse'
            />

            <button
              type='button'
              onClick={resyncWithoutImages}
              className='push-from-ns text-white px-6 py-2 bg-lightblue-600 rounded-full h-max hover:bg-lightblue-700 flex justify-center items-center relative w-full smallMobile:w-[210px] min-h-10'
              disabled={imageSyncLoading || !selectedTender}
            >
              {imageSyncLoading ? <Spinner style={{ color: 'white' }} /> : 'Resync without images'}
            </button>
          </div>
        </div>
      ) : null}

      {selectedTender && (
        <ImageManagementTable
          thead={deleteImageThead}
          tbody={tenderItemsForDelete}
          theadChild={deleteImageTheadChild}
          tbodyChild={tenderItemsForDeleteChild}
          childIndex={childIndex}
          setChildIndex={setChildIndex}
          tableCellWidth={tableCellWidth}
          styledReserveNotMet
          onDeleteAll={onDeleteAll}
          items={mediaManagement.data.galleryList || []}
          tenderId={selectedTender.id}
        />
      )}
    </>
  )
}
